/**
 * OpportunityDetail — Main opportunity management page.
 *
 * Integrates:
 * - MEDDICC Scorecard with scoring visualization
 * - Close Checklist for action items
 * - Deal Notes timeline
 * - One-click SF export of full update
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Building2, DollarSign, Target, Calendar,
  Copy, Check, Loader2, AlertCircle
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import MeddpicScorecard from './MeddpicScorecard';
import CloseChecklist from './CloseChecklist';
import DealNotes from './DealNotes';
import CopyButton from './CopyButton';
import './OpportunityDetail.css';

function formatCurrency(value) {
  if (!value) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const STAGE_COLORS = {
  prospecting: 'var(--color-text-muted)',
  qualification: 'var(--color-primary)',
  needs_analysis: 'var(--color-primary)',
  proposal: 'var(--color-warning)',
  negotiation: 'var(--color-warning)',
  closed_won: 'var(--color-success)',
  closed_lost: 'var(--color-error)',
};

function OpportunityDetail() {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch opportunity with all related data
  const { data: opportunity, isLoading, error } = useQuery({
    queryKey: ['opportunity', opportunityId],
    queryFn: () => apiClient.get(`/api/opportunities/${opportunityId}`),
    enabled: !!opportunityId,
  });

  // Fetch SF export text
  const { data: exportData } = useQuery({
    queryKey: ['opportunity', opportunityId, 'export'],
    queryFn: () => apiClient.get(`/api/opportunities/${opportunityId}/export`),
    enabled: !!opportunityId,
  });

  const handleUpdate = () => {
    queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
    queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId, 'export'] });
  };

  if (isLoading) {
    return (
      <div className="od-loading">
        <Loader2 size={32} className="od-spinner" />
        <span>Loading opportunity...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="od-error">
        <AlertCircle size={32} />
        <h3>Failed to load opportunity</h3>
        <p>{error.message}</p>
        <button className="od-btn od-btn-primary" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="od-error">
        <AlertCircle size={32} />
        <h3>Opportunity not found</h3>
        <button className="od-btn od-btn-primary" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  const stageLabel = opportunity.stage?.replace(/_/g, ' ') || 'Unknown';
  const stageColor = STAGE_COLORS[opportunity.stage] || 'var(--color-text-muted)';

  return (
    <div className="od-container">
      {/* Header */}
      <div className="od-header">
        <button className="od-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="od-header-content">
          <div className="od-header-main">
            <h1 className="od-title">{opportunity.name}</h1>
            <span
              className="od-stage"
              style={{ backgroundColor: `${stageColor}20`, color: stageColor }}
            >
              {stageLabel}
            </span>
          </div>

          <div className="od-meta">
            {opportunity.account_name && (
              <div className="od-meta-item">
                <Building2 size={14} />
                <span>{opportunity.account_name}</span>
              </div>
            )}
            <div className="od-meta-item">
              <DollarSign size={14} />
              <span>{formatCurrency(opportunity.value)}</span>
            </div>
            {opportunity.close_date && (
              <div className="od-meta-item">
                <Calendar size={14} />
                <span>Close: {formatDate(opportunity.close_date)}</span>
              </div>
            )}
            {opportunity.meddpicc_score !== null && (
              <div className="od-meta-item">
                <Target size={14} />
                <span>MEDDICC: {opportunity.meddpicc_score}%</span>
              </div>
            )}
          </div>
        </div>

        <div className="od-header-actions">
          {exportData?.export_text && (
            <CopyButton
              text={exportData.export_text}
              label="Copy Full Update"
              showLabel
              className="copy-btn-pill od-copy-full"
            />
          )}
        </div>
      </div>

      {/* Main content grid */}
      <div className="od-grid">
        {/* Left column - MEDDICC Scorecard */}
        <div className="od-column od-column-left">
          <MeddpicScorecard
            opportunityId={opportunityId}
            scores={{
              metrics: opportunity.metrics_score,
              economic_buyer: opportunity.economic_buyer_score,
              decision_criteria: opportunity.decision_criteria_score,
              decision_process: opportunity.decision_process_score,
              identify_pain: opportunity.identify_pain_score,
              champion: opportunity.champion_score,
              competition: opportunity.competition_score,
            }}
            details={opportunity.meddpicc_details || {}}
            overallScore={opportunity.meddpicc_score}
            onUpdate={handleUpdate}
          />
        </div>

        {/* Right column - Checklist and Notes */}
        <div className="od-column od-column-right">
          <CloseChecklist
            opportunityId={opportunityId}
            checklist={opportunity.checklist || []}
            progress={opportunity.checklist_progress}
            onUpdate={handleUpdate}
          />

          <DealNotes
            opportunityId={opportunityId}
            notes={opportunity.notes || []}
            onUpdate={handleUpdate}
          />
        </div>
      </div>
    </div>
  );
}

export default OpportunityDetail;
